/**
 * Renovator's own additions to the kai-shiki styleguide tokens.
 *
 * Three of these - `styleguideFontSize`, `styleguideColors` and `styleguideFontWeight` - spread
 * a kai-shiki core object and add values kai-shiki does not carry. They override no key, so a
 * shared name resolves to the same value whichever side you import it from, and
 * `kaiShikiTokens.test.ts` fails if that ever stops being true. Note the names shadow kai-shiki's
 * own: importing `styleguideColors` from `@kaiizen/kai-shiki/core` gets the unextended palette.
 *
 * The seven `KAI_SHIKI_*` objects are renovator's own, built from core values but spreading
 * nothing, so the no-override guarantee does not apply to them (KAIDEV-4398).
 */
import { pxToRem } from '@/Client/Styles/pxToRem';
import { css } from '@emotion/react';
import {
  colors,
  figmaColorScales,
  figmaSemanticColors,
  styleguideColorsExtended,
  styleguideFontSize as styleguideFontSizeCore,
  styleguideFontWeight as styleguideFontWeightCore,
} from '@kaiizen/kai-shiki/core';

/** The styleguide type sizes, plus the input size kai-shiki does not carry yet. */
export const styleguideFontSize = {
  ...styleguideFontSizeCore,
  /** Body · Input (14): form field values. */
  bodyInput: 14,
} as const;

/**
 * The styleguide palette, plus the one selection tone the 2026 navigator
 * redesign needed and kai-shiki does not carry yet.
 *
 * Hover and selection are two tints of the same lemon so that a selected row
 * under the cursor still reads as selected — one shared value would have made
 * hovering an unselected row indistinguishable from selecting it.
 */
export const styleguideColors = {
  ...styleguideColorsExtended,

  /**
   * Selected-row wash (#FDFDD6) — one step deeper than `wash` (#FDFDEA), which
   * is hover. Carries selection at every level of the Kai Navigator: the scope
   * header, a group header, and a task row.
   */
  selectedWash: '#FDFDD6',

  /**
   * The scope editor's product-row grounds, both paper blended with bone: a
   * candidate line (not in the scope yet) sits on the light one, an in-scope
   * line on the heavier one.
   */
  candidateTint: '#FAF7F0',
  scopedTint: '#E9E4D9',

  /*
   * Kai_Styleguide.html's info pair and success fill, which kai-shiki does not
   * carry yet: info is the guide's one blue — in-review / edit-requested /
   * in-progress / neutral status — and the fills are the paired light chip
   * backgrounds its status pills sit on.
   */
  info: '#1F76B8',
  infoFill: '#E1EFFA',
  successFill: '#DDF1E1',
  errorFill: '#F6E6E4',

  /**
   * Page canvas behind cards ("shell") — the 2026 account/profile mockups'
   * background tone, which kai-shiki does not carry yet.
   */
  shell: '#EFEAE1',

  /** Section band (#F9F7F0) — the heading rows that split a table into sections, a shade under paper. */
  sectionBand: '#F9F7F0',
} as const;

/**
 * The styleguide weights, plus `medium` — kai-shiki carries only 400 and 700,
 * but CircularXX ships a Medium face and the 2026 styleguide sets amounts at it.
 *
 * These are exactly the three weights the app has faces for (see
 * `typography.css`). There is deliberately no 600: no such face exists, so a
 * `semibold` token would resolve to 700 and quietly misrepresent itself. Where
 * the mockups say 600, use `bold` — that is what 600 renders as.
 */
export const styleguideFontWeight = {
  ...styleguideFontWeightCore,

  /** Amounts and other numerics — present, but not competing with titles. */
  medium: 500,
} as const;

const GRID_SIZE = 10;
const TYPOGRAPHY_FONT_FAMILY = 'CircularXX, -apple-system, BlinkMacSystemFont, "Avenir Next", "Segoe UI", Roboto, sans-serif';

const TYPOGRAPHY_FONT_WEIGHT = {
  normal: 400,
  bold: 700,
} as const;

const TYPOGRAPHY_FONT_SIZE = {
  tiny: 10,
  xSmall: 12,
  small: 14,
  medium: 16,
  large: 18,
  xLarge: 20,
} as const;

const createTypographyStyle = (
  fontSize: number,
  fontWeight: (typeof TYPOGRAPHY_FONT_WEIGHT)[keyof typeof TYPOGRAPHY_FONT_WEIGHT],
) =>
  css`
  font-family: ${TYPOGRAPHY_FONT_FAMILY};
  font-size: ${pxToRem(fontSize)};
  font-weight: ${fontWeight};
`;

const CARRY_FORWARD = {
  disabledButtonBorder: '#bababa',
  infoBlueLegacy: '#2878b8',
  infoBlueLegacyStrong: '#1c6196',
  dangerRedLegacy: '#bf3b3b',
  dangerBackgroundLegacy: '#faecec',
  dividerLegacy: '#e0e0e0',
  iconMutedLegacy: '#b7b7b7',
} as const;

export const KAI_SHIKI_LAYOUT = {
  spacing: GRID_SIZE,
  spacingHalf: GRID_SIZE / 2,
  spacing1: GRID_SIZE,
  spacing1p5: GRID_SIZE * 1.5,
  spacing2: GRID_SIZE * 2,
  spacing2p5: GRID_SIZE * 2.5,
  spacing3: GRID_SIZE * 3,
  spacing4: GRID_SIZE * 4,
  spacing5: GRID_SIZE * 5,
  spacing6: GRID_SIZE * 6,
  leftNavWidth: GRID_SIZE * 23,
  topBarHeight: GRID_SIZE * 6,
} as const;

export const KAI_SHIKI_TYPOGRAPHY = {
  fontFamily: TYPOGRAPHY_FONT_FAMILY,
  fontSize: TYPOGRAPHY_FONT_SIZE,
  fontWeight: TYPOGRAPHY_FONT_WEIGHT,
  xLargeBold: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.xLarge,
    TYPOGRAPHY_FONT_WEIGHT.bold,
  ),
  xLarge: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.xLarge,
    TYPOGRAPHY_FONT_WEIGHT.normal,
  ),
  largeBold: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.large,
    TYPOGRAPHY_FONT_WEIGHT.bold,
  ),
  large: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.large,
    TYPOGRAPHY_FONT_WEIGHT.normal,
  ),
  mediumBold: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.medium,
    TYPOGRAPHY_FONT_WEIGHT.bold,
  ),
  medium: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.medium,
    TYPOGRAPHY_FONT_WEIGHT.normal,
  ),
  smallBold: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.small,
    TYPOGRAPHY_FONT_WEIGHT.bold,
  ),
  small: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.small,
    TYPOGRAPHY_FONT_WEIGHT.normal,
  ),
  xSmallBold: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.xSmall,
    TYPOGRAPHY_FONT_WEIGHT.bold,
  ),
  xSmall: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.xSmall,
    TYPOGRAPHY_FONT_WEIGHT.normal,
  ),
  tinyBold: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.tiny,
    TYPOGRAPHY_FONT_WEIGHT.bold,
  ),
  tiny: createTypographyStyle(
    TYPOGRAPHY_FONT_SIZE.tiny,
    TYPOGRAPHY_FONT_WEIGHT.normal,
  ),
} as const;

export const KAI_SHIKI_ALWAYS_DARK_COLORS = {
  background: {
    black: '#000000',
    dark: '#1a1a1a',
    light: '#222222',
  },
  text: {
    darkGray: '#888888',
    green: '#7cb88e',
    white: '#ffffff',
  },
  border: {
    darkGray: '#333333',
  },
  primary: figmaColorScales.yellow[500],
} as const;

export const KAI_SHIKI_ORG_SETTINGS_COLORS = {
  pageBackground: figmaSemanticColors.surface.subtle,
  white: colors.white,
  text: figmaSemanticColors.text.primary,
  textMuted: figmaSemanticColors.text.secondary,
  textMutedLight: figmaSemanticColors.text.tertiary,
  textSoft: figmaColorScales.neutral[400],
  textFieldLabel: figmaColorScales.neutral[500],
  border: figmaSemanticColors.border.subtle,
  panelHeaderBackground: figmaSemanticColors.surface.subtle,
  infoBlue: CARRY_FORWARD.infoBlueLegacy,
  infoBlueBackground: figmaColorScales.blue[50],
  green: figmaSemanticColors.intent.success.default,
  red: CARRY_FORWARD.dangerRedLegacy,
  disabledButtonBorder: CARRY_FORWARD.disabledButtonBorder,
} as const;

export const KAI_SHIKI_SETTINGS_WIDGET_COLORS = {
  white: KAI_SHIKI_ORG_SETTINGS_COLORS.white,
  text: KAI_SHIKI_ORG_SETTINGS_COLORS.text,
  textSoft: KAI_SHIKI_ORG_SETTINGS_COLORS.textSoft,
  textMutedLight: KAI_SHIKI_ORG_SETTINGS_COLORS.textMutedLight,
  textFieldLabel: KAI_SHIKI_ORG_SETTINGS_COLORS.textFieldLabel,
  border: KAI_SHIKI_ORG_SETTINGS_COLORS.border,
  panelHeaderBackground: KAI_SHIKI_ORG_SETTINGS_COLORS.panelHeaderBackground,
  infoBlue: KAI_SHIKI_ORG_SETTINGS_COLORS.infoBlue,
  infoBlueBackground: KAI_SHIKI_ORG_SETTINGS_COLORS.infoBlueBackground,
  green: figmaSemanticColors.intent.success.default,
  disabledButtonBorder: KAI_SHIKI_ORG_SETTINGS_COLORS.disabledButtonBorder,
  mainNavigationActionColor: styleguideColorsExtended.accent,
  mainNavigationActionHoverColor: styleguideColorsExtended.accentDeep,
  mainNavigationActionTextColor: styleguideColorsExtended.ink,
} as const;

export const KAI_SHIKI_MODAL_COLORS = {
  white: colors.white,
  text: figmaSemanticColors.text.primary,
  textFieldLabel: figmaColorScales.neutral[500],
  textMuted: figmaSemanticColors.text.secondary,
  iconMuted: CARRY_FORWARD.iconMutedLegacy,
  border: figmaSemanticColors.border.subtle,
  divider: CARRY_FORWARD.dividerLegacy,
  green: figmaSemanticColors.intent.success.default,
  infoBlue: CARRY_FORWARD.infoBlueLegacyStrong,
  infoBlueBackground: figmaColorScales.blue[50],
  dangerRed: CARRY_FORWARD.dangerRedLegacy,
  dangerBackground: CARRY_FORWARD.dangerBackgroundLegacy,
} as const;

export const KAI_SHIKI_MODAL_EFFECTS = {
  overlay: 'rgba(0, 0, 0, 0.45)',
  cardShadow: '0 12px 40px rgba(0, 0, 0, 0.16)',
} as const;
